import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { PLAYER_REGISTRATION_STORAGE_KEY } from '@/constants/playerStorageKeys'
import {
  isAccessibilityServiceEnabled,
  isDeviceOwnerApp
} from '@/helpers/androidAccessibility'
import { isBootSplashPresent } from '@/helpers/bootSplash'
import { getPlatform, getPlatformInstance } from '@/platform'

export const ACCESSIBILITY_SETUP_SKIPPED_KEY = 'LUCIMARK:ACCESSIBILITY_SETUP_SKIPPED'

/** Same backup key as AuthenticationService — survives selective primary wipes on Android. */
const REGISTRATION_BACKUP_KEY = 'LUCIMARK:REGISTRATION:BACKUP'

/** While the overlay is up, re-check tenant registration (Studio claim) and a11y. */
const OVERLAY_REEVAL_MS = 1_500

function readSkipped(): boolean {
  try {
    return localStorage.getItem(ACCESSIBILITY_SETUP_SKIPPED_KEY) === '1'
  }
  catch {
    return false
  }
}

function writeSkipped(): void {
  try {
    localStorage.setItem(ACCESSIBILITY_SETUP_SKIPPED_KEY, '1')
  }
  catch {
    // ignore quota / private mode
  }
}

/**
 * True when persisted registration is bound to a tenant (production screen).
 * Pairing rows (`tenantId` null) and missing storage → false.
 */
export function isTenantBoundRegistrationRaw(raw: string | null | undefined): boolean {
  if (raw == null || raw === '') {
    return false
  }
  try {
    const parsed = JSON.parse(raw) as { tenantId?: unknown }
    return parsed.tenantId != null && parsed.tenantId !== ''
  }
  catch {
    return false
  }
}

/**
 * Gate only for Device Owner pairing / first boot — not for already-registered
 * production screens (APK OTA must not interrupt playback with this overlay).
 */
export function isAccessibilityRemoteSetupEligible(opts: {
  deviceOwner: boolean
  tenantRegistered: boolean
}): boolean {
  return opts.deviceOwner && !opts.tenantRegistered
}

export async function readTenantRegistered(): Promise<boolean> {
  const storage = getPlatformInstance().storage
  try {
    const primary = await storage.get(PLAYER_REGISTRATION_STORAGE_KEY)
    if (isTenantBoundRegistrationRaw(primary)) {
      return true
    }
    const backup = await storage.get(REGISTRATION_BACKUP_KEY)
    return isTenantBoundRegistrationRaw(backup)
  }
  catch {
    return false
  }
}

/**
 * Gate for Android Device Owner installs during pairing: show Accessibility
 * remote-setup overlay until enabled or skipped. Hidden on web / non-DO /
 * already tenant-registered / already skipped.
 */
export function useAccessibilityRemoteSetupGate() {
  const eligible = ref(false)
  const accessibilityOn = ref(false)
  const skipped = ref(readSkipped())
  const splashGone = ref(!isBootSplashPresent())

  const showOverlay = computed(
    () =>
      eligible.value
      && !accessibilityOn.value
      && !skipped.value
      && splashGone.value
  )

  async function reevaluate(): Promise<void> {
    if (getPlatform() !== 'android') {
      eligible.value = false
      return
    }
    const [deviceOwner, a11y, tenantRegistered] = await Promise.all([
      isDeviceOwnerApp(),
      isAccessibilityServiceEnabled(),
      readTenantRegistered()
    ])
    eligible.value = isAccessibilityRemoteSetupEligible({ deviceOwner, tenantRegistered })
    accessibilityOn.value = a11y
  }

  function skip(): void {
    writeSkipped()
    skipped.value = true
  }

  function markDone(): void {
    accessibilityOn.value = true
  }

  let overlayPoll: ReturnType<typeof setInterval> | null = null

  function stopOverlayPoll(): void {
    if (overlayPoll != null) {
      clearInterval(overlayPoll)
      overlayPoll = null
    }
  }

  onMounted(() => {
    void reevaluate()
    if (isBootSplashPresent()) {
      const tick = setInterval(() => {
        if (!isBootSplashPresent()) {
          splashGone.value = true
          clearInterval(tick)
        }
      }, 200)
      // Safety: never block forever if splash helpers misreport.
      setTimeout(() => {
        splashGone.value = true
        clearInterval(tick)
      }, 15_000)
    }
  })

  onBeforeUnmount(() => {
    stopOverlayPoll()
  })

  // Re-run while visible so a mid-overlay Studio claim (tenant registration) dismisses
  // the gate without Skip / Accessibility / app restart.
  watch(showOverlay, (show) => {
    stopOverlayPoll()
    if (!show) {
      return
    }
    void reevaluate()
    overlayPoll = setInterval(() => {
      void reevaluate()
    }, OVERLAY_REEVAL_MS)
  })

  return {
    showOverlay,
    skip,
    markDone,
    reevaluate
  }
}
